import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Check } from 'lucide-react';
import { programIncludes } from '../mock';

export const WhatsIncluded3D = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <section ref={ref} className="relative bg-dark py-24 md:py-32 px-6 overflow-hidden">
      <motion.div
        className="absolute top-20 left-20 w-40 h-40 border border-[#c6a04a]/10"
        animate={{
          rotate: [0, 360],
          scale: [1, 1.2, 1]
        }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        className="absolute bottom-20 right-20 w-32 h-32 border border-[#d9be7e]/10"
        animate={{
          rotate: [360, 0],
          scale: [1.2, 1, 1.2]
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div 
          className="text-center mb-20 space-y-6"
          initial={{ opacity: 0, y: 100 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.p 
            className="font-accent text-xl md:text-2xl italic text-gold"
            initial={{ opacity: 0, scale: 0 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            What's Included
          </motion.p>
          <motion.h2 
            className="font-hero text-3xl md:text-5xl lg:text-6xl uppercase tracking-wide text-cream"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
            style={{ fontWeight: 700 }}
          >
            Everything You Need.
            <br />
            <span className="text-gold">Nothing You Don't.</span>
          </motion.h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {programIncludes.map((item, index) => (
            <IncludeCard key={index} item={item} index={index} inView={inView} />
          ))}
        </div>
      </div>
    </section>
  );
};

const IncludeCard = ({ item, index, inView }) => {
  const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 });
  const cardRef = React.useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 20;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -20;
    setMousePosition({ x, y });
  };

  const handleMouseLeave = () => {
    setMousePosition({ x: 0, y: 0 });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 100, rotateY: -45, scale: 0.8 }}
      animate={inView ? { opacity: 1, y: 0, rotateY: 0, scale: 1 } : {}}
      transition={{ 
        delay: index * 0.1,
        duration: 0.8,
        type: "spring",
        stiffness: 100
      }}
      style={{ perspective: '1500px' }}
    >
      <motion.div
        ref={cardRef}
        className="bg-panel p-8 border-2 border-[#c6a04a]/30 h-full cursor-pointer group relative overflow-hidden"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        animate={{
          rotateX: mousePosition.y * 0.3,
          rotateY: mousePosition.x * 0.3,
        }}
        whileHover={{ 
          borderColor: 'rgba(198, 160, 74, 0.8)',
          boxShadow: '0 30px 60px rgba(198, 160, 74, 0.3)',
          scale: 1.02
        }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        style={{ transformStyle: 'preserve-3d' }}
      >
        <motion.div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{
            background: `radial-gradient(circle at ${mousePosition.x * 5 + 50}% ${mousePosition.y * 5 + 50}%, rgba(198, 160, 74, 0.15), transparent 50%)`,
          }}
        />

        <motion.div
          className="w-12 h-12 mb-6 relative flex items-center justify-center"
          style={{ transform: 'translateZ(40px)' }}
          whileHover={{ rotate: 360, scale: 1.2 }}
          transition={{ duration: 0.6 }}
        >
          <div className="absolute inset-0 border-2 border-gold transform rotate-45" />
          <Check className="w-6 h-6 text-gold relative z-10" />
        </motion.div>

        <div style={{ transform: 'translateZ(20px)' }}>
          <h3 className="font-display text-xl md:text-2xl uppercase tracking-tight text-cream mb-3 group-hover:text-gold transition-colors duration-300">
            {item.title}
          </h3>
          <p className="font-body text-sm md:text-base text-[#e8e6df]/80 leading-relaxed font-light">
            {item.description}
          </p>
        </div>

        <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-gold/40 group-hover:border-gold transition-colors duration-300" />
        <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-gold/40 group-hover:border-gold transition-colors duration-300" />
      </motion.div>
    </motion.div>
  );
};
